import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RealtimeClient } from './RealtimeClient';
import { RealtimeEvent } from './types';

type Ctx = {
  client: RealtimeClient | null;
  isConnected: boolean;
};

const RealtimeContext = createContext<Ctx>({ client: null, isConnected: false });

type RealtimeProviderProps = React.PropsWithChildren<{
  baseUrl: string;
  token?: string | null;
}>;

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({
  baseUrl,
  token,
  children,
}) => {
  const [client, setClient] = useState<RealtimeClient | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const realtimeClient = new RealtimeClient(baseUrl);
    realtimeClient.onEvent((evt: RealtimeEvent) => {
      if (evt?.type === 'ping') return;
    });
    setClient(realtimeClient);

    // Connect with token if available, otherwise connect anonymously
    realtimeClient.connect(token || '');

    const id = setInterval(
      () => setIsConnected(!!realtimeClient?.isConnected),
      1000,
    );
    return () => {
      clearInterval(id);
      realtimeClient.disconnect();
    };
  }, [baseUrl, token]);

  const value = useMemo(
    () => ({ client, isConnected }),
    [client, isConnected],
  );
  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);

export const useSubscribeTopic = (
  topic: string,
  handler: (evt: RealtimeEvent) => void,
) => {
  const { client } = useRealtime();
  useEffect(() => {
    if (!client || !topic) return;
    const off = client.onEvent(evt => {
      handler(evt);
    });
    client.subscribe([topic]);
    return () => {
      client.unsubscribe([topic]);
      off();
    };
  }, [client, topic, handler]);
};
